import React, { useState } from 'react';
import { AlertBehaviorType, AlertBehaviorKey, FrequencyWindow } from './behaviorTypes';

export type AlertMetricType = 'frequency' | 'duration' | 'activity';

export interface AlertLogEntry {
  id: string;
  type: AlertBehaviorType;
  severity: 'warning' | 'critical';
  title: string;
  message: string;
  detectedAt: Date;
  threshold: number;        // the threshold value that was exceeded
  actualValue: number;      // the actual measured value
  metricType: AlertMetricType;  // which metric triggered the alert
  behaviorKey: AlertBehaviorKey; // which behavior config triggered
  contactNotified: boolean;
  acknowledged: boolean;
  frequencyWindow?: FrequencyWindow;  // window for frequency alerts (e.g. 1hr, 30min, 1day)
}

interface AlertHistoryPanelProps {
  alerts: AlertLogEntry[];
  onViewReport: (alert: AlertLogEntry) => void;
  // NEW (Phase 3): view-mode toggle + loading state
  viewMode?: 'persisted' | 'simulated';
  onViewModeChange?: (mode: 'persisted' | 'simulated') => void;
  isLoading?: boolean;
  // NEW (Phase 4): whether behavior data is loaded (for simulated empty state messaging)
  hasData?: boolean;
}

const AlertHistoryPanel: React.FC<AlertHistoryPanelProps> = ({
  alerts,
  onViewReport,
  viewMode = 'persisted',
  onViewModeChange,
  isLoading = false,
  hasData = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const unacknowledgedCount = alerts.filter(a => !a.acknowledged).length;
  const criticalCount = alerts.filter(a => a.severity === 'critical').length;

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const getIcon = (type: AlertBehaviorType) => {
    switch (type) {
      case 'spin': return '🔄';
      case 'toilet': return '🚽';
      case 'rest': return '😴';
      case 'activity': return '📉';
      case 'scratch': return '✋';
      case 'lying': return '😴';
      case 'sitting': return '🪑';
      case 'standing': return '🧍';
      case 'walk': return '🚶';
      default: return '⚠️';
    }
  };

  const getSeverityBadge = (severity: string) => {
    if (severity === 'critical') {
      return (
        <span className="px-2 py-0.5 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded">
          CRITICAL
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded">
        WARNING
      </span>
    );
  };

  // Group alerts by date
  const groupedAlerts = alerts.reduce((groups, alert) => {
    const dateKey = formatDate(alert.detectedAt);
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(alert);
    return groups;
  }, {} as Record<string, AlertLogEntry[]>);

  return (
    <div className="rounded-xl bg-white dark:bg-claude-darkSurface border border-claude-border dark:border-claude-darkBorder overflow-hidden">
      {/* Header */}
      <div
        className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-claude-darkSurfaceHover transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <h3 className="font-medium text-gray-900 dark:text-claude-darkText">
              Alert History
            </h3>
          </div>
          {unacknowledgedCount > 0 && (
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
                {unacknowledgedCount} new
              </span>
              {criticalCount > 0 && (
                <span className="px-2 py-0.5 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full">
                  {criticalCount} critical
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Mode toggle — only shown if onViewModeChange is provided */}
          {onViewModeChange && (
            <div
              className="flex items-center bg-gray-100 dark:bg-slate-700 rounded-lg p-0.5"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => onViewModeChange('persisted')}
                title="Alerts saved to the database when thresholds were exceeded. Immune to threshold changes."
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${
                  viewMode === 'persisted'
                    ? 'bg-white dark:bg-slate-500 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-white'
                }`}
              >
                📋 Persisted
              </button>
              <button
                onClick={() => onViewModeChange('simulated')}
                title="Alerts recalculated using current thresholds against loaded data. Not saved to database."
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${
                  viewMode === 'simulated'
                    ? 'bg-white dark:bg-slate-500 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-white'
                }`}
              >
                🔍 Simulated
              </button>
            </div>
          )}
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {isLoading ? 'Loading...' : `${alerts.length} alerts`}
          </span>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-gray-100 dark:border-gray-800 max-h-80 overflow-y-auto">
          {/* Simulated mode banner — always shown in simulated mode so the user
              knows they're viewing ephemeral recalculation, even when empty */}
          {viewMode === 'simulated' && (
            <div className="px-4 py-2 bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800/30">
              <p className="text-xs text-yellow-700 dark:text-yellow-300">
                ⚠ Simulated — recalculated from current thresholds. Not saved to database.
              </p>
            </div>
          )}

          {isLoading ? (
            <div className="p-8 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 dark:bg-claude-darkBg flex items-center justify-center">
                <svg className="w-6 h-6 text-gray-400 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Loading alert history...
              </p>
            </div>
          ) : alerts.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 dark:bg-claude-darkBg flex items-center justify-center">
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {viewMode === 'simulated'
                  ? (hasData
                      ? 'No alerts detected with current thresholds for the loaded data.'
                      : 'No behavior data loaded for the selected time range.')
                  : 'No alerts in history. Alerts will appear here when thresholds are exceeded.'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {Object.entries(groupedAlerts).map(([date, dateAlerts]) => (
                <div key={date}>
                  <div className="px-4 py-2 bg-gray-50 dark:bg-claude-darkBg">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      {date}
                    </span>
                  </div>
                  {dateAlerts.map(alert => (
                    <div
                      key={alert.id}
                      className={`px-4 py-3 hover:bg-gray-50 dark:hover:bg-claude-darkSurfaceHover cursor-pointer transition-colors ${
                        !alert.acknowledged ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''
                      }`}
                      onClick={() => onViewReport(alert)}
                    >
                      <div className="flex items-start gap-3">
                        <span className="text-lg">{getIcon(alert.type)}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-sm text-gray-900 dark:text-claude-darkText truncate">
                              {alert.title}
                            </span>
                            {getSeverityBadge(alert.severity)}
                            {!alert.acknowledged && (
                              <span className="w-2 h-2 bg-blue-500 rounded-full" />
                            )}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
                            {alert.message}
                          </p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-400 dark:text-gray-500">
                            <span>{formatTime(alert.detectedAt)}</span>
                            {alert.contactNotified && (
                              <span className="flex items-center gap-1">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                Contact notified
                              </span>
                            )}
                          </div>
                        </div>
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AlertHistoryPanel;
